import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Archive,
  Bell,
  BellOff,
  Camera,
  Palette,
  LogOut,
  MoreVertical,
  Pencil,
  Trash2,
  UserMinus,
  UserPlus,
} from "lucide-react";
import { Drawer } from "@/components/ui/Drawer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLocale } from "@/hooks/useLocale";
import {
  useAddChatMembersMutation,
  useArchiveChatConversationMutation,
  useLeaveChatConversationMutation,
  useMuteChatConversationMutation,
  useRemoveChatMemberMutation,
  useSetChatImageMutation,
  useUpdateChatConversationMutation,
} from "@/services/(my-space)/api.chat";
import { chatHub } from "@/services/websocket/chat-hub";
import { getErrorMessage } from "@/utils/error.utils";
import { useImagePreview } from "@/context/ImagePreview";
import { cn } from "@/utils/cn";
import {
  CHAT_MUTE_OPTIONS,
  type ChatMuteDuration,
  type IChatConversationDetail,
  type IChatMember,
} from "@/types/(my-space)/chat.interface";
import { ChatAvatar } from "./ChatAvatar";
import { ProfilePopup } from "./ProfilePopup";
import { PeoplePickerDialog } from "./PeoplePickerDialog";
import { DeleteChatDialog } from "./DeleteChatDialog";
import { ChannelNoticeSettings } from "./ChannelNoticeSettings";
import { WallpaperDialog } from "./WallpaperDialog";
import { MediaGallery } from "./MediaGallery";
import { chatWallpaperSpec } from "../_lib/chat-wallpapers";
import {
  chatWorkStatusOption,
  conversationTitle,
  memberCountLabel,
  muteDurationLabel,
  personName,
  presenceLabel,
} from "../_lib/chat-labels";
import { usePresenceVersion } from "../_hooks/usePresence";
import { channelOf, channelSubtitle, isOfficialChannel } from "../_lib/channel-notices";
import { VerifiedBadge } from "./VerifiedBadge";

interface ChatInfoDrawerProps {
  open: boolean;
  detail: IChatConversationDetail;
  myUserId: number | undefined;
  onClose: () => void;
  onMessageUser: (userId: number) => void;
}

const isAdminRole = (role: string) => role === "owner" || role === "admin";

export const ChatInfoDrawer = ({
  open,
  detail: d,
  myUserId,
  onClose,
  onMessageUser,
}: ChatInfoDrawerProps) => {
  const { t } = useTranslation("chat");
  const { formatDate } = useLocale();
  const navigate = useNavigate();
  usePresenceVersion();

  const [mute] = useMuteChatConversationMutation();
  const [archive] = useArchiveChatConversationMutation();
  const [leave, { isLoading: isLeaving }] = useLeaveChatConversationMutation();
  const [setImage, { isLoading: isUploadingImage }] = useSetChatImageMutation();
  const [addMembers, { isLoading: isAdding }] = useAddChatMembersMutation();
  const [removeMember] = useRemoveChatMemberMutation();

  const [renameOpen, setRenameOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [toRemove, setToRemove] = useState<IChatMember | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [wallpaperOpen, setWallpaperOpen] = useState(false);
  const wallpaper = chatWallpaperSpec(d.effectiveWallpaper);
  const imageInput = useRef<HTMLInputElement>(null);
  // The drawer must not close behind the previewer when the photo is clicked away.
  const openPreview = useImagePreview();
  const [previewing, setPreviewing] = useState(false);
  // The avatar popup, over the drawer (which must not close under it either).
  const [profileOpen, setProfileOpen] = useState(false);
  const openImages = (urls: string[], index: number) => {
    setPreviewing(true);
    openPreview(urls, () => setPreviewing(false), index);
  };

  const isDirect = d.type === "direct";
  const isChannel = d.type === "channel";
  const isSelf = !!d.isSelf;
  const amAdmin = !isDirect && isAdminRole(d.myRole);
  const title = conversationTitle(t, d);
  const peer = d.peer;

  // A self chat's peer is me: no presence or work status to report.
  const channel = channelOf(d);
  const subtitle = isChannel
    ? channelSubtitle(t, channel)
    : isSelf
    ? t("labels.self_subtitle")
    : peer
      ? !peer.isActive
        ? t("labels.account_inactive")
        : presenceLabel(t, formatDate, chatHub.getPresence(peer.id)) ??
          chatWorkStatusOption(t, peer.workStatus)
      : memberCountLabel(t, d);

  /** Owner removes anyone but themselves; an admin removes plain members — as the API. */
  const canRemove = (m: IChatMember) => {
    if (isDirect || m.id === myUserId || m.role === "owner") return false;
    if (d.myRole === "owner") return true;
    return d.myRole === "admin" && m.role === "member";
  };

  const run = async (fn: () => Promise<unknown>, errorKey: string, success?: string) => {
    try {
      await fn();
      if (success) toast.success(success);
      return true;
    } catch (e) {
      toast.error(getErrorMessage(e, t(errorKey)));
      return false;
    }
  };

  const members = useMemo(
    () =>
      [...d.members].sort((a, b) => {
        const rank = (m: IChatMember) =>
          m.id === myUserId ? 0 : m.role === "owner" ? 1 : m.role === "admin" ? 2 : 3;
        return rank(a) - rank(b) || personName(t, a).localeCompare(personName(t, b));
      }),
    [d.members, myUserId, t],
  );

  const muteLabel = d.isMuted
    ? d.mutedUntil
      ? t("mute.muted_until", { date: formatDate(new Date(d.mutedUntil), "PPp") })
      : t("mute.muted")
    : t("mute.on");

  return (
    <Drawer
      isOpen={open}
      onClose={onClose}
      title={isChannel ? t("channel.settings.drawer_title") : t("info.title")}
      size="md"
      isDismissable={!previewing && !profileOpen}
    >
      <div className="flex flex-col gap-5 pb-6">
        {/* Header */}
        <div className="flex flex-col items-center gap-2 text-center">
          {/* The picture opens the avatar popup (as in the list); an admin's
              camera badge changes it. */}
          <div className="relative">
            <button
              type="button"
              // A channel has no profile to show.
              disabled={isChannel}
              onClick={() => setProfileOpen(true)}
              className="rounded-full disabled:cursor-default"
              aria-label={t("profile.open")}
            >
              <ChatAvatar
                name={title}
                imageUrl={isDirect ? peer?.profileImage : d.imageUrl}
                kind={d.type}
                channel={channel}
                size="xl"
                isOnline={isDirect && !isSelf && !!chatHub.getPresence(peer?.id)?.online}
              />
            </button>
            {amAdmin ? (
              <button
                type="button"
                disabled={isUploadingImage}
                onClick={() => imageInput.current?.click()}
                aria-label={t("info.change_picture")}
                title={t("info.change_picture")}
                className="absolute end-0 bottom-0 flex size-7 items-center justify-center rounded-full bg-primary text-primary-foreground ring-2 ring-background disabled:opacity-60"
              >
                <Camera className="size-3.5" />
              </button>
            ) : null}
          </div>
          <ProfilePopup
            conversation={profileOpen ? d : null}
            onClose={() => setProfileOpen(false)}
            onOpenImage={(url) => openImages([url], 0)}
          />
          <div className="flex items-center gap-1">
            <h2 className="flex items-center justify-center gap-1.5 text-lg font-semibold text-foreground">
              {title}
              {isOfficialChannel(channel) ? <VerifiedBadge className="size-5" /> : null}
            </h2>
            {amAdmin ? (
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={() => setRenameOpen(true)}
                aria-label={t("info.rename")}
                title={t("info.rename")}
              >
                <Pencil className="size-3.5" />
              </Button>
            ) : null}
          </div>
          {subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
          <input
            ref={imageInput}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) void run(() => setImage({ id: d.id, image: file }).unwrap(), "errors.change_picture");
            }}
          />
        </div>

        {/* Notifications — a self chat never pushes, so there's nothing to mute */}
        {!isSelf ? (
          <Section>
            <div className="flex items-center gap-3 px-3 py-2.5">
              <IconTile>{d.isMuted ? <BellOff className="size-4" /> : <Bell className="size-4" />}</IconTile>
              <span className="flex-1 text-sm font-medium text-foreground">{t("mute.notifications")}</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    {muteLabel}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {d.isMuted ? (
                    <DropdownMenuItem
                      onSelect={() =>
                        void run(() => mute({ id: d.id, duration: "off" }).unwrap(), "errors.mute")
                      }
                    >
                      <Bell className="size-4" />
                      {t("mute.unmute")}
                    </DropdownMenuItem>
                  ) : null}
                  {CHAT_MUTE_OPTIONS.map((duration: ChatMuteDuration) => (
                    <DropdownMenuItem
                      key={duration}
                      onSelect={() =>
                        void run(() => mute({ id: d.id, duration }).unwrap(), "errors.mute")
                      }
                    >
                      <BellOff className="size-4" />
                      {muteDurationLabel(t, duration)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </Section>
        ) : null}

        {isChannel ? <ChannelNoticeSettings channel={channel} /> : null}

        {/* Background */}
        <Section>
          <button
            type="button"
            onClick={() => setWallpaperOpen(true)}
            className="flex w-full items-center gap-3 px-3 py-2.5 text-start hover:bg-muted"
          >
            <IconTile>
              <Palette className="size-4" />
            </IconTile>
            <span className="flex-1 text-sm font-medium text-foreground">{t("wallpaper.title")}</span>
            <span className="text-xs text-muted-foreground">
              {t(`wallpaper.names.${wallpaper.key}`)}
            </span>
            <span
              className={cn(
                "size-7 shrink-0 rounded-sm border border-border",
                !wallpaper.style && "bg-muted/30",
              )}
              style={wallpaper.style}
            />
          </button>
        </Section>

        {/* Photo gallery — a channel's notices carry no media */}
        {isChannel ? null : <MediaGallery conversationId={d.id} onOpenImage={openImages} />}

        {/* Members — broadcast recipients get no member list from the API */}
        {!isDirect && !isChannel && d.members.length ? (
          <Section
            title={t("receipts.section_count", {
              title: d.type === "broadcast" ? t("info.recipients") : t("info.members"),
              count: d.members.length,
            })}
          >
            {amAdmin ? (
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                className="flex w-full items-center gap-3 border-b border-border px-3 py-2.5 text-start hover:bg-muted"
              >
                <IconTile>
                  <UserPlus className="size-4" />
                </IconTile>
                <span className="text-sm font-medium text-primary">{t("info.add_members")}</span>
              </button>
            ) : null}
            <ul>
              {members.map((m) => {
                const online = !!chatHub.getPresence(m.id)?.online;
                return (
                  <li
                    key={m.id}
                    className="flex items-center gap-3 border-b border-border px-3 py-2 last:border-b-0"
                  >
                    <button
                      type="button"
                      disabled={m.id === myUserId}
                      onClick={() => onMessageUser(m.id)}
                      className="flex min-w-0 flex-1 items-center gap-3 text-start"
                    >
                      <ChatAvatar name={personName(t, m)} imageUrl={m.profileImage} size="md" isOnline={online} />
                      <span className="truncate text-sm text-foreground">
                        {m.id === myUserId ? t("info.you_label") : personName(t, m)}
                      </span>
                    </button>
                    {m.role !== "member" ? (
                      <Badge variant={m.role === "owner" ? "primary" : "neutral"}>
                        {m.role === "owner" ? t("info.role_owner") : t("info.role_admin")}
                      </Badge>
                    ) : null}
                    {canRemove(m) ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-xs" aria-label={t("actions.more")}>
                            <MoreVertical className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="text-danger focus:text-danger"
                            onSelect={() => setToRemove(m)}
                          >
                            <UserMinus className="size-4" />
                            {t("info.remove")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </Section>
        ) : null}

        {/* Archive / leave */}
        <Section>
          <ActionRow
            icon={<Archive className="size-4" />}
            label={d.isArchived ? t("info.unarchive") : t("info.archive")}
            onClick={() =>
              void run(
                () => archive({ id: d.id, archived: !d.isArchived }).unwrap(),
                "errors.archive",
                d.isArchived ? t("info.unarchived_toast") : t("info.archived_toast"),
              )
            }
          />
          <ActionRow
            icon={<Trash2 className="size-4" />}
            label={t("info.delete_chat")}
            danger
            onClick={() => setConfirmDelete(true)}
          />
          {!isDirect && !isChannel ? (
            <ActionRow
              icon={<LogOut className="size-4 rtl-flip" />}
              label={t("info.leave")}
              danger
              onClick={() => setConfirmLeave(true)}
            />
          ) : null}
        </Section>
      </div>

      <WallpaperDialog
        open={wallpaperOpen}
        detail={d}
        onClose={() => setWallpaperOpen(false)}
      />

      <DeleteChatDialog
        conversationId={confirmDelete ? d.id : null}
        isSelf={isSelf}
        onClose={() => setConfirmDelete(false)}
        onDeleted={() => {
          onClose();
          navigate("/chat", { replace: true });
        }}
      />

      <RenameDialog
        open={renameOpen}
        current={d.name ?? ""}
        conversationId={d.id}
        onClose={() => setRenameOpen(false)}
      />

      <PeoplePickerDialog
        open={addOpen}
        mode="multiple"
        title={t("info.add_members")}
        excludeIds={d.members.map((m) => m.id)}
        isSubmitting={isAdding}
        onClose={() => setAddOpen(false)}
        onConfirm={async (people) => {
          const ok = await run(
            () => addMembers({ id: d.id, userIds: people.map((p) => p.id) }).unwrap(),
            "errors.add_members",
          );
          if (ok) setAddOpen(false);
        }}
      />

      <AlertDialog open={confirmLeave} onOpenChange={setConfirmLeave}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("info.leave_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("info.leave_message")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("composer.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={isLeaving}
              className="bg-danger text-danger-foreground hover:bg-danger/90"
              onClick={async () => {
                const ok = await run(() => leave(d.id).unwrap(), "errors.leave");
                if (ok) {
                  onClose();
                  navigate("/chat", { replace: true });
                }
              }}
            >
              {t("info.leave")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!toRemove} onOpenChange={(v) => !v && setToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("info.remove_member_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {toRemove ? t("info.remove_member_message", { name: personName(t, toRemove) }) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("composer.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-danger text-danger-foreground hover:bg-danger/90"
              onClick={() => {
                const target = toRemove;
                setToRemove(null);
                if (target) {
                  void run(
                    () => removeMember({ id: d.id, userId: target.id }).unwrap(),
                    "errors.remove_member",
                  );
                }
              }}
            >
              {t("info.remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Drawer>
  );
};

const Section = ({ title, children }: { title?: string; children: React.ReactNode }) => (
  <section className="flex flex-col gap-2">
    {title ? (
      <h3 className="px-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">{title}</h3>
    ) : null}
    <div className="overflow-hidden rounded-md border border-border bg-surface">{children}</div>
  </section>
);

const IconTile = ({ children, danger }: { children: React.ReactNode; danger?: boolean }) => (
  <span
    className={cn(
      "flex size-9 shrink-0 items-center justify-center rounded-full",
      danger ? "bg-danger-muted text-danger" : "bg-primary-muted text-primary",
    )}
  >
    {children}
  </span>
);

const ActionRow = ({
  icon,
  label,
  danger,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  danger?: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center gap-3 border-b border-border px-3 py-2.5 text-start last:border-b-0 hover:bg-muted"
  >
    <IconTile danger={danger}>{icon}</IconTile>
    <span className={cn("text-sm font-medium", danger ? "text-danger" : "text-foreground")}>{label}</span>
  </button>
);

/** Save is disabled until the name changes; a failure keeps the dialog open. */
const RenameDialog = ({
  open,
  current,
  conversationId,
  onClose,
}: {
  open: boolean;
  current: string;
  conversationId: number;
  onClose: () => void;
}) => {
  const { t } = useTranslation("chat");
  const [name, setName] = useState(current);
  const [update, { isLoading }] = useUpdateChatConversationMutation();

  useEffect(() => {
    if (open) setName(current);
  }, [current, open]);

  const trimmed = name.trim();
  const canSave = !!trimmed && trimmed !== current.trim();

  const save = async () => {
    if (!canSave) return;
    try {
      await update({ id: conversationId, name: trimmed }).unwrap();
      onClose();
    } catch (e) {
      toast.error(getErrorMessage(e, t("errors.rename")));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("info.rename_title")}</DialogTitle>
        </DialogHeader>
        <Input
          value={name}
          maxLength={120}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && void save()}
          aria-label={t("info.rename")}
          className="h-10"
          autoFocus
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("composer.cancel")}
          </Button>
          <Button variant="primary" disabled={!canSave} isLoading={isLoading} onClick={() => void save()}>
            {t("info.rename")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
